import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import moment from "moment";
import { useAppContext, usePersistedState } from "../../../../AppContext";
import mainAxios from "../../../../services/mainAxios";
import Loader from "../../../Layout/Loader";
import PrimeTable from "../../../PrimeTable/PrimeTable";
import DateRange from "../../../global/DateRange";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import func from "../../../../services/functions";
import ExportCsvMultiple from "../../../global/ExporCsvMultiple";
import { globalFilterStatisticCard } from "../../../global/StatisticsCards";
import { useGeneratePayRollColumns } from "./generatePayRollColumns";
import usePayrollStatistics from "./usePayrollStatistics";
import { toast } from "react-toastify";

const PayRollGrid = () => {
  const { t, i18n } = useTranslation("translations");
  const [payRollls, setPayRollls] = useState(null);
  const [selectedPayRolls, setSelectedPayRolls] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    user: [user],
    selectedCompany: [selectedCompany],
    company: [fullCompany],
    dates: [dates, setDates],
    reloadData: [reloadData],
  } = useAppContext();

  const company = user.companiesRights.find(
    (company) => company.id === parseInt(selectedCompany)
  );

  const [loading, setLoading] = useState(false);

  const [expanded, setExpanded] = useState({
    additionalInfo: [],
    expandedRows: null,
    loadingAdditionalInfo: false,
    expandedInvoiceRows: [],
    expandedInvoiceData: null,
    loadingInvoiceData: false,
  });

  const defaultExpand = {
    additionalInfo: [],
    expandedRows: null,
    loadingAdditionalInfo: false,
    expandedInvoiceRows: [],
    expandedInvoiceData: null,
    loadingInvoiceData: false,
  };

  const filterParams = {};

  const defaultFilters = {
    fromDate: dates.fromDate,
    toDate: dates.toDate,
    params: {
      ...filterParams,
    },
  };
  const [filters, setFilters] = useState(defaultFilters);

  const { statistics, statisticsLoading, refreshStatistics, getProfiscStatusOptions, getBankStatusOptions, getCurrencyOptions } = usePayrollStatistics({
    filters,
    t,
  });

  const profiscStatusOptions = getProfiscStatusOptions();
  const bankStatusOptions = getBankStatusOptions();
  const currencyOptions = getCurrencyOptions();

  React.useEffect(() => {
    getFilteredResults({
      data: { ...defaultFilters, params: { ...defaultFilters.params } },
      pageSize: 10,
      page: 1,
    });
  }, [selectedCompany, reloadData]);

  const [payRollForCsv, setPayRollForCsv] = useState([]);

  const defaultSortCriteria = {
    field: "recordDate",
    direction: "DESC",
  };

  const [tableRows, setTableRows] = useState({
    totalRows: 0,
    rowsPerPage: 10,
    currentPage: 1,
    sortCriteria: defaultSortCriteria,
  });
  const pdfContainer = useRef();

  const getFilteredResults = ({
    data,
    pageSize = 10,
    page = 1,
    clearFilters = false,
    getPayRollForCsv = false,
    sortCriteria = [defaultSortCriteria],
  } = {}) => {
    const requestFilters = data ? data : filters;

    let requestBody = {
      object: "GetSalaryOrder",
      fromDate: moment(filters.fromDate).format("YYYY-MM-DD"),
      toDate: moment(filters.toDate).format("YYYY-MM-DD"),
      ...requestFilters,
      params: {
        ...requestFilters.params,
      },
      pagination: getPayRollForCsv
        ? null
        : {
            pageSize,
            pageNumber: page,
          },
      sortCriteria,
    };

    return mainAxios
      .post("/salary-orders/search", requestBody)
      .then((res) => {
        setLoading(false);
        if (res?.data?.status === true) {
          if (getPayRollForCsv) {
            setPayRollForCsv(res.data.content);
          } else {
            setPayRollls(res.data.content);
            setTableRows((tableRows) => ({
              ...tableRows,
              totalRows: res?.data?.totalSize,
            }));
          }
          return true;
        }
        return false;
      })
      .catch((err) => {
        setLoading(false);
        return false;
      });
  };

  const handleParamsChange = (e, label, val) => {
    const key = e ? e.target.name : label;
    const value = e ? e.target.value : val;
    setFilters((filters) => ({
      ...filters,
      params: {
        ...filters.params,
        [key]: value,
      },
    }));
  };

  let columns = useGeneratePayRollColumns({
    getFilteredResults,
    tableRows,
    handleParamsChange,
    filters,
    setFilters,
    refreshStatistics
  });

  const [selectedColumns, setSelectedColumns] = usePersistedState(
    columns.map((col) => ({
      field: col.field,
      show: !!col.default,
      ...(col?.persisted ? { persisted: true } : {}),
    })),
    "payRollCols2"
  );

  const additionalInfoColumns = [
    {
      field: "",
      header: "",
      style: { maxWidth: 40 },
    },
    {
      header: t("payRoll.successActions"),
      field: "successActions",
      style: { minWidth: 300, maxWidth: 300 },
      body: (row) => (
        <span>
          <span className={row?.successCount > 0 ? "text-success" : "text-danger"}>
            {row?.successCount ?? 0}
          </span>
          {" / "}
          {row?.totalCount ?? 0}
        </span>
      ),
    },
    {
      header: t("payRoll.confirmedAmount"),
      field: "confirmedAmount",
      style: { minWidth: 300, maxWidth: 300 },
      body: (row) => (
        <span>
          <span className={row?.confirmedAmount > 0 ? "text-success" : "text-danger"}>
            {func.getFormattedMoneyValue(row?.confirmedAmount ?? 0)}
          </span>
          {" / "}
          {func.getFormattedMoneyValue(row?.totalAmount ?? 0)}
        </span>
      ),
      align: "right",
    },
    {
      field: "",
      header: "",
      style: { width: 2500 },
    },
  ];

  const onRowCollapse = () => {
    setExpanded(defaultExpand);
  };

  const rowExpansionTemplate = () => {
    if ( expanded?.loadingAdditionalInfo)
      return <Loader size="small" />;
    return (
      <div className="d-flex flex-column ml-3 mt-3">
        <div className="mb-5" style={{ width: "100vw" }}>
          <h4>{t("payRoll.additionalInfo")}</h4>
          <PrimeTable
            loading={expanded?.loadingAdditionalInfo}
            value={expanded.relatedInvoice}
            columns={additionalInfoColumns}
            showFilters={false}
            paginator={false}
            filterDisplay={false}
            responsiveLayout="scroll"
          />
        </div>
      </div>
    );
  };

  const additionalInfoOnExpand = (event) => {
    setExpanded((state) => ({
      ...state,
      loadingAdditionalInfo: true,
      expandedRows: [event.data],
    }));
    mainAxios
      .post("/salary-orders/search", {
        object: "GetSalaryOrderAdditionalInfo",
        value: event?.data?.requestId,
        username: user.username,
        company: parseInt(selectedCompany),
      })
      .then((res) => {
        setExpanded((state) => ({ ...state, loadingAdditionalInfo: false }));
        if (res?.data?.status) {
          setExpanded((state) => ({
            ...state,
            relatedInvoice: res?.data?.content,
          }));
        }
      })
      .catch((err) =>
        setExpanded((state) => ({ ...state, loadingAdditionalInfo: false }))
      );
  };

  const onRowExpand = (event) => {
    additionalInfoOnExpand(event);
  };

  const hasAcceptedPayrolls = selectedPayRolls.some(
    (payroll) => payroll?.workflowStatus?.name === "DRAFT"
  );

  const filterStatisticCard = (typeOptions, props) =>
    globalFilterStatisticCard(typeOptions, {
      ...props,
      handleParamsChange,
      getFilteredResults,
      user,
      filters,
    });

    const handleRowSelect = (event) => {
      const rowData = event.data;
      if (rowData?.workflowStatus?.name === "DRAFT") {
        setSelectedPayRolls((prevSelected) =>
          prevSelected.includes(rowData)
            ? prevSelected.filter((order) => order !== rowData)
            : [...prevSelected, rowData]
        );
      }
    };

    const handleApproveMultiple = async () => {
      if (!hasAcceptedPayrolls || actionLoading) return;
  
      setActionLoading(true);
      const payrollIds = selectedPayRolls.map((payroll) => payroll.id);
  
      try {
        const response = await mainAxios.post("/salary-orders/approve-multiple-salary", {
          content: payrollIds,
          nuis: company?.nuis,
        });
  
        if (response?.data?.status) {
          toast.success(t("payRoll.approveMultiSuccess"), {
            containerId: "all_toast",
          });
          setSelectedPayRolls([]);
          getFilteredResults({
            data: { ...filters, params: { ...filters.params } },
            pageSize: tableRows.rowsPerPage,
            page: tableRows.currentPage,
          });
        } else {
          toast.error(t("payRoll.approveMultiFailed"), {
            containerId: "all_toast",
          });
        }
      } catch (error) {
        toast.error(t("payRoll.approveMultiFailed"), {
          containerId: "all_toast",
        });
      } finally {
        setActionLoading(false);
      }
    };

    const selectedPayRollsCounts = (
    <span className="btn btn-sm btn-outline-primary">
        {selectedPayRolls.length > 0 ? `${t("invoice.selected")} ${selectedPayRolls.length}` : ""}
    </span>
  );
  const headerButtonsLeft = selectedPayRolls.length > 0 && selectedPayRollsCounts;

  
    const multiSelectBody = (rowData) =>
      rowData?.workflowStatus?.name === "DRAFT" ? (
        <Checkbox
          onChange={() => handleRowSelect({ data: rowData })}
          checked={selectedPayRolls.includes(rowData)}
        />
      ) : null;
  
    const headerButtonsRight = (
      <div className="d-flex align-items-center mt-4">
        {hasAcceptedPayrolls && (
          <button
            onClick={handleApproveMultiple}
            disabled={actionLoading}
            className="btn btn-sm btn-outline-primary mr-3"
          >
            {actionLoading ? <Loader size="xs" /> : t("payRoll.approveMulti")}
          </button>
        )}
      </div>
    );
  
    const sentMultiPayRollFilter = useMemo(() => {
      const payrollsForSentPayment =
        payRollls?.filter((rowData) => rowData?.workflowStatus?.name === "DRAFT") ?? [];
      if (payrollsForSentPayment?.length === 0) return <></>;
      return (
        <Checkbox
          onChange={(e) => {
            if (e.checked) {
              const _selectedRows = [];
              payrollsForSentPayment.forEach((reciept) =>
                _selectedRows.push(reciept)
              );
              setSelectedPayRolls(_selectedRows);
            } else {
              setSelectedPayRolls([]);
            }
          }}
          checked={
            selectedPayRolls.length === payrollsForSentPayment?.length &&
            selectedPayRolls.length !== 0
          }
        />
      );
    }, [payRollls, selectedPayRolls]);

  // Export dropdown for headerIcons
  const exportDropdown = (
    <ExportCsvMultiple
      data={payRollForCsv}
      selectedColumns={selectedColumns}
      columns={columns}
      onClick={() => getFilteredResults({ getPayRollForCsv: true })}
      excludedColumns={["status", "actions", "print"]}
      asDropdown={true}
    />
  );

  return payRollls ? (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center my-2">
        <div className="left-header d-flex align-items-center">
          <h3 className="mb-0 p-0">{t("payRoll.payRoll")}</h3>
          {[1, 2, 3, 4, 5].includes(user.role.id) && (
            <Link to={"/create-payroll"} className="btn btn-sm btn-primary ml-3">
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t("navbar.create")}
            </Link>
          )}
        </div>
        <div className="mb-4 mb-md-0">
          <DateRange
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            onChange={(dates) => {
              let fromDate = dates[0];
              let toDate = dates[1];
              if (fromDate && toDate) {
                getFilteredResults({
                  data: {
                    ...filters,
                    fromDate,
                    toDate,
                    params: { ...filters.params },
                  },
                });
              }
              setFilters((filters) => ({
                ...defaultFilters,
                fromDate,
                toDate,
              }));
              setDates({ fromDate, toDate });
            }}
          />
        </div>
      </div>

      <div className="row px-1 d-none d-md-flex">
        <ul className="col-12 col-sm-6 col-md-4 p-2 mb-1">
          {filterStatisticCard(profiscStatusOptions, {
            title: t("payRoll.profiscStatus"),
            cardClasses: "stats-cols-2",
          })}
        </ul>
        <ul className="col-12 col-sm-6 col-md-4 p-2 mb-1">
          {filterStatisticCard(bankStatusOptions, {
            title: t("payRoll.bankStatus"),
            cardClasses: "stats-cols-2",
          })}
        </ul>
        <ul className="col-12 col-sm-6 col-md-2 p-2 mb-1">
          {filterStatisticCard(currencyOptions, {
            title: t("invoice.currency"),
            cardClasses: "stats-cols-1",
          })}
        </ul>
      </div>
      <div>
            <PrimeTable
              showFilters={true}
              reorderableColumns={true}
              filters={[filters, setFilters]}
              defaultFilters={defaultFilters}
              tableRows={[tableRows, setTableRows]}
              value={payRollls ?? []}
              gridKey="payRollCols2"
              selectedColumns={[selectedColumns, setSelectedColumns]}
              columns={columns}
              expandedRows={expanded?.expandedRows ?? []}
              onRowExpand={onRowExpand}
              onRowCollapse={onRowCollapse}
              headerButtonsRight={headerButtonsRight}
              headerButtonsLeft={headerButtonsLeft}
              headerIcons={exportDropdown}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="ublId"
              onRemoteDataChange={(remoteData) => {
                const updatedFilters = remoteData.filters ?? filters;
                const updatedTableData = remoteData.tableRows ?? tableRows;
                const { currentPage, rowsPerPage } = updatedTableData;
                getFilteredResults({
                  data: updatedFilters,
                  pageSize: rowsPerPage,
                  page: currentPage,
                });
              }}
              onRowSelect={handleRowSelect}
              onClearFilter={() => {
                getFilteredResults({
                  data: { ...filters, params: { ...defaultFilters.params } },
                  pageSize: 10,
                  rowsPerPage: 1,
                  clearFilters: true,
                });
              }}
              columnDeps={[expanded, fullCompany]}
              loading={loading}
              mobileExpansionLabel="Payroll Details"
              mobileFilterContent={
                <div className="filter-cards-row">
                  <div className="filter-card-col">
                    {filterStatisticCard(profiscStatusOptions, {
                      title: t("payRoll.profiscStatus"),
                      cardClasses: "stats-cols-2",
                    })}
                  </div>
                  <div className="filter-card-col">
                    {filterStatisticCard(bankStatusOptions, {
                      title: t("payRoll.bankStatus"),
                      cardClasses: "stats-cols-2",
                    })}
                  </div>
                  <div className="filter-card-col">
                    {filterStatisticCard(currencyOptions, {
                      title: t("invoice.currency"),
                      cardClasses: "stats-cols-1",
                    })}
                  </div>
                </div>
              }
            >
              <Column
                body={multiSelectBody}
                filterElement={sentMultiPayRollFilter}
                showFilterMenu={false}
                showClearButton={false}
                filter={true}
              />
              <Column
                expander={() => true}
                style={{ width: "0.5em" }}
                header=""
                field="expansion"
              />
            </PrimeTable>
      </div>
    </>
  ) : (
    <Loader />
  );
};

export default PayRollGrid;

